import moment from "moment";
import "moment-timezone";
import React from "react";
import { Modal } from "../../components";
import { Panel } from "../../components/panels";
import { type IPagination, Pagination } from "../../pagination";
import TaskCreateModal, { type TaskState } from "../create/app";
import UserTaskRowCard from "./TaskRow";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import { systemTimezone } from "utilities";

interface Props {
  mainArea: server.dto.MainAreas
  config: server.dto.LinkedUserTaskPanelConfig
}

const UserTasksPanel = (props: Props) => {
  const getDefaultTaskState = (): TaskState => ({
    charactersRemaining: serverReference.constants.userTask.maxBodyLength,
    assignedTo: site.currentUser,
    deadlineLocalTimezone: moment.utc().tz(systemTimezone).startOf("minute"),
    visibleToExternal: props.config.defaultVisibleToExternal,
    reminderInPopup: true,
    reminderByEmail: true,
    subject: "",
    body: "",
    subjects: [],
    noUserError: false,
    noSubjectError: false,
    bodyTooLongError: false,
    dateOutsideAllowedRangeError: false,
    isLoadingNoteConversion: false,
    isSaving: false,
    offsetDays: 0,
    isRelativeDeadline: false
  });

  const [taskState, setTaskState] = React.useState(getDefaultTaskState());
  const [tasks, setTasks] = React.useState<server.dto.UserTask[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editingTask, setEditingTask] = React.useState<server.dto.UserTask>(undefined);
  const [pagination, setPagination] = React.useState<IPagination>({
    total: 0,
    page: 1,
    perPage: 10,
    maxPerPage: 10,
    pageCount: 0
  });

  const updateTask = (partial: Partial<TaskState>) => {
    setTaskState(prevTask => ({
      ...prevTask,
      ...partial
    }));
  };

  const reloadTasks = async (page = 1) => {
    setLoading(true);
    setPagination({ ...pagination, page });
    const result = await window.CoreApi.UserTask.List({
      mainArea: props.mainArea,
      page,
      perPage: pagination.perPage,
      parentId: props.config.itemId
    });
    setPagination(result.data.pagination);
    setTasks(result.data.data);
    setLoading(false);
  };

  const onFinishedSaving = async () => {
    setEditingTask(undefined);
    setTaskState(getDefaultTaskState());
    return reloadTasks();
  };

  const onFinishedConvertingToNote = async () => {
    setEditingTask(undefined);
    setTaskState(getDefaultTaskState());

    if (window.refreshNotes) {
      window.refreshNotes();
    }
    return reloadTasks();
  };

  const onNewTask = () => {
    setEditingTask(null);
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "task",
    label: props.config.headerLabel,
    totalItems: tasks.length,
    onAddClicked: props.config.canAdd ? onNewTask : undefined
  });

  const onReminderEmailChanged = async (task: server.dto.UserTask, reminderEmail: boolean) => {
    const idx = tasks.findIndex(t => t.userTaskId === task.userTaskId);
    const newTask = { ...tasks[idx], reminderEmail };
    const newTasks = Object.assign([], tasks, { [idx]: newTask });
    setTasks(newTasks);
  };

  const onReminderPopupChanged = async (task: server.dto.UserTask, reminderPopup: boolean) => {
    const idx = tasks.findIndex(t => t.userTaskId === task.userTaskId);
    const newTask = { ...tasks[idx], reminderPopup };
    const newTasks = Object.assign([], tasks, { [idx]: newTask });
    setTasks(newTasks);
  };

  const onTaskEdit = (taskToBeEdited: server.dto.UserTask) => {
    let taskState = getDefaultTaskState();
    taskState = {
      ...taskState,
      assignedTo: taskToBeEdited.assignedTo,
      deadlineLocalTimezone: moment.utc(taskToBeEdited.deadlineSource).tz(systemTimezone),
      isRelativeDeadline: taskToBeEdited.deadlineType === "relativeToShowDate",
      offsetDays: taskToBeEdited.deadlineOffsetInDays ?? 0,
      visibleToExternal: taskToBeEdited.visibleToExternal,
      reminderInPopup: taskToBeEdited.reminderPopup,
      reminderByEmail: taskToBeEdited.reminderEmail,
      subject: taskToBeEdited.title || "",
      body: taskToBeEdited.body || ""
    };
    setTaskState(taskState);
    setEditingTask(taskToBeEdited);
  };

  const onClose = () => {
    if (editingTask) {
      setTaskState(getDefaultTaskState());
    }
    setEditingTask(undefined);
  };

  return <>
    <Panel
      panelClassName="s1_related-feature-panel"
      button={
        props.config.canAdd && <ButtonSecondary onClick={onNewTask} label={RESX.Buttons.btnNew} />
      }
      panelRef={refToScrollTo}
      title={props.config.headerLabel}
      isLoading={loading}
      hasData={tasks.length > 0}
      pagination={<Pagination
        paginationData={pagination}
        onPaginate={reloadTasks}
      />}
    >
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {tasks.map(task => <UserTaskRowCard
            key={task.userTaskId}
            task={task}
            onEdit={() => { onTaskEdit(task); }}
            onDeleted={reloadTasks}
            canDelete={task.permissions.canDelete}
            canEdit={task.permissions.canModify}
            config={props.config}
            onReminderEmailChanged={onReminderEmailChanged}
            onReminderPopupChanged={onReminderPopupChanged}
          />)}
        </div>
      </div>
    </Panel>
    <Modal
      title={editingTask === null ? RESX.Task.NewTask : RESX.Task.EditTask}
      handleClose={onClose}
      width="large"
      gap="bigger"
      isOpen={editingTask !== undefined}
      hasAutoScrollBox
      minHeight={400}
      height={650}
    >
      <TaskCreateModal
        mainArea={props.mainArea}
        config={props.config}
        existingTask={editingTask}
        onFinishedSaving={onFinishedSaving}
        onConvertedToNote={onFinishedConvertingToNote}
        taskState={taskState}
        onTaskChanged={updateTask}
        canChooseRelativeDeadline={props.mainArea === "show"}
      />
    </Modal>
  </>;
};

export default UserTasksPanel;